import AsyncStorage from '@react-native-async-storage/async-storage';
import { captureException } from '@/utils/sentry';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { Platform } from 'react-native';

/**
 * Storage key for experience mode preference
 */
export const EXPERIENCE_STORAGE_KEY = '@experience_mode';

/**
 * Experience modes available in the application
 */
export enum ExperienceMode {
  FINANCE_HUB = 'FINANCE_HUB',
  OPERATOR_PORTAL = 'OPERATOR_PORTAL',
  SALES_PORTAL = 'SALES_PORTAL',
}

interface ExperienceState {
  /** Flag to prevent double-switching (instant switch, prevents UI double-clicks) */
  isTransitioning: boolean;
  /** Current experience mode */
  mode: ExperienceMode;
}

interface ExperienceActions {
  /**
   * Clean up experience data on logout
   * Resets to operator portal and clears storage
   */
  cleanupOnLogout: () => void;
  /**
   * Switch to a different experience mode
   * Persists to AsyncStorage automatically via persist middleware
   */
  switchToExperience: (
    newMode: ExperienceMode,
    options?: {
      /** Skip the web URL reset to '/'. Used only by the storefront
       *  boot-forcing path, which must preserve the deep-link URL. */
      skipUrlReset?: boolean;
    },
  ) => void;
}

type ExperienceStore = ExperienceState & ExperienceActions;

export const useExperienceStore = create<ExperienceStore>()(
  persist(
    (set, get) => ({
      // State
      isTransitioning: false,
      mode: ExperienceMode.OPERATOR_PORTAL,

      // Actions
      // eslint-disable-next-line sort-keys/sort-keys-fix
      cleanupOnLogout: () => {
        // Reset to operator portal (persist middleware handles storage)
        set({
          isTransitioning: false,
          mode: ExperienceMode.OPERATOR_PORTAL,
        });
      },
      switchToExperience: (newMode, options) => {
        const { mode } = get();

        // Already in this mode
        if (mode === newMode) {
          return;
        }

        try {
          // On web, reset the URL to '/' when leaving an experience that owns
          // a path (e.g. Sales Portal, Finance Hub) so the next experience
          // doesn't inherit the previous area's route. The operator portal
          // lives at '/', so leaving it needs no reset
          if (
            Platform.OS === 'web' &&
            (mode === ExperienceMode.SALES_PORTAL ||
              mode === ExperienceMode.FINANCE_HUB) &&
            !options?.skipUrlReset
          ) {
            window.history.pushState({}, '', '/');
          }

          // Set transitioning flag and update mode
          // Persist middleware automatically saves to AsyncStorage
          set({ isTransitioning: true });
          set({
            isTransitioning: false,
            mode: newMode,
          });
        } catch (error) {
          captureException(error);
          // Reset transitioning flag on error
          set({ isTransitioning: false });
          throw error;
        }
      },
    }),
    {
      name: EXPERIENCE_STORAGE_KEY,
      storage: createJSONStorage(() => AsyncStorage),
    },
  ),
);
